import React, { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react';

export enum ThemeMode {
    Dark = 'dark',
    Light = 'light'
}

export type ThemeConfig = {
    mode: ThemeMode;
};

export const DEFAULT_THEME_MODE = ThemeMode.Light;
const defaultValue = {
    mode: DEFAULT_THEME_MODE
};
export const ThemeContext = createContext<ThemeConfig>(defaultValue);
export const THEME_DATA_ATTRIBUTE = 'data-theme';
export const getThemeSelectorProps = (mode?: ThemeMode) => ({
    [THEME_DATA_ATTRIBUTE]: mode
});

export type ThemeProviderProps = {
    children: ReactNode;
} & Partial<ThemeConfig>;

export const ThemeProvider = ({ mode: modeProps, children }: ThemeProviderProps) => {
    const parentContextValue = useContext(ThemeContext);
    const mode = (modeProps ?? parentContextValue.mode) || defaultValue.mode;

    const setRootThemeAttributes = (newMode?: ThemeMode) => {
        if (typeof window !== 'undefined') {
            const attributes = getThemeSelectorProps(newMode);
            Object.keys(attributes).forEach((key) => {
                const value = attributes[key as keyof typeof attributes];
                if (value === undefined) {
                    document.documentElement.removeAttribute(key);
                } else {
                    document.documentElement.setAttribute(key, value);
                }
            });
        }
    };

    const contextValue = useMemo(() => {
        return {
            mode
        };
    }, [mode]);

    useEffect(() => {
        setRootThemeAttributes(mode);
    }, [mode]);

    return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};
